import { useEffect, useState } from 'react'
import { Flag, LanguageFlag } from '@/shared/places/Flag'
import { usePlaces } from '@/shared/places/customerFacts'
import { nf } from '@/shared/util/numbers'
import { formatDate } from '@/shared/util/dates'
import {
  countMetric,
  loadSeries,
  type BucketUnit,
  type MetricDef,
  type Window,
} from './repository/periodMetricsRepository'
import {
  loadMetricRows,
  metricDateLabel,
  METRIC_PAGE,
  type MetricPage,
} from './repository/metricRowsRepository'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'


const UNIT_NOTE: Record<BucketUnit, string> = {
  hour: 'one point per hour',
  day: 'one point per day',
  week: 'one point per week',
  month: 'one point per month',
}

/**
 * A metric's development over the selected period.
 *
 * An SVG polyline rather than a chart library: one series, no interaction
 * beyond hover, and the whole thing is forty lines. Bucketing is decided by the
 * period's length (see `bucketsFor`), so nobody has to choose a granularity —
 * a day reads hourly and five years reads monthly.
 */
export function MetricChartModal({
  metric,
  window: win,
  rangeLabel,
  onClose,
}: {
  metric: MetricDef
  window: Window
  rangeLabel: string
  onClose: () => void
}) {
  const [points, setPoints] = useState<{ label: string; value: number }[] | null>(null)
  /** The box's own figure — not the sum of the buckets. See below. */
  const [periodTotal, setPeriodTotal] = useState<number | null>(null)
  const [unit, setUnit] = useState<BucketUnit>('day')
  const [error, setError] = useState<string | null>(null)

  /**
   * The people behind the figure, a page at a time.
   *
   * `cursors[n]` is the document to start page `n` after, so `cursors[0]` is
   * always null and going back is free — no query is repeated and no document is
   * skipped over. See the note on pagination in `metricRowsRepository`.
   */
  const [page, setPage] = useState<MetricPage | null>(null)
  const [pageIndex, setPageIndex] = useState(0)
  // Country and language per person on this page, from RevenueCat — 28 Oct 2026.
  const places = usePlaces(page?.rows.map((r) => r.uid ?? '') ?? [])
  const [cursors, setCursors] = useState<(QueryDocumentSnapshot<DocumentData> | null)[]>([null])
  const [rowsLoading, setRowsLoading] = useState(true)
  const [rowsError, setRowsError] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    let cancelled = false
    setPoints(null)
    setPeriodTotal(null)
    setError(null)
    // The header shows the same number as the box, queried the same way. It is
    // not the sum of the buckets, because a bucketed series can only contain
    // what has a date — and for New users all time, ~17,000 accounts do not.
    countMetric(metric.key, win, Date.now())
      .then((n) => {
        if (!cancelled) setPeriodTotal(n)
      })
      .catch(() => {})
    loadSeries(metric.key, win)
      .then((r) => {
        if (cancelled) return
        setUnit(r.unit)
        setPoints(r.points)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Query failed.')
      })
    return () => {
      cancelled = true
    }
  }, [metric.key, win])

  // Back to page one whenever the metric or the period changes — page four of
  // the previous question is not page four of this one.
  useEffect(() => {
    setPageIndex(0)
    setCursors([null])
  }, [metric.key, win])

  useEffect(() => {
    let cancelled = false
    setRowsLoading(true)
    setRowsError(null)
    loadMetricRows(metric.key, win, Date.now(), cursors[pageIndex] ?? null)
      .then((p) => {
        if (cancelled) return
        setPage(p)
        setRowsLoading(false)
        // Remember where the next page starts, so Next is one query and Previous
        // is none. Recorded here rather than on click: the cursor is a fact about
        // the page that just arrived.
        if (p.cursor) {
          setCursors((cur) => {
            if (cur[pageIndex + 1] !== undefined) return cur
            const next = [...cur]
            next[pageIndex + 1] = p.cursor
            return next
          })
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setRowsLoading(false)
        setRowsError(err instanceof Error ? err.message : 'Could not read the records.')
      })
    return () => {
      cancelled = true
    }
    // `cursors` is deliberately not a dependency: it is written by this effect,
    // and depending on it would re-run the query on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric.key, win, pageIndex])

  const charted = points?.reduce((n, p) => n + p.value, 0) ?? 0
  const total = periodTotal ?? charted
  const undated = Math.max(0, total - charted)
  const peak = Math.max(1, ...(points ?? []).map((p) => p.value))

  // A fixed viewBox with preserveAspectRatio="none" lets the chart stretch to
  // whatever width the dialog gets without recomputing anything on resize.
  const W = 1000
  const H = 260
  /**
   * Inset, so the first and last points are not drawn on the frame.
   *
   * The stroke is `non-scaling-stroke`, so half of a 2.5px line sitting exactly
   * on x=0 or x=W falls outside a container with `overflow: hidden` and the
   * chart looks cut off at both ends. The vertical mapping has always had this
   * padding; the horizontal one did not.
   */
  const PAD = 14
  const path = (points ?? [])
    .map((p, i, arr) => {
      const x = arr.length === 1 ? W / 2 : PAD + (i / (arr.length - 1)) * (W - PAD * 2)
      const y = H - (p.value / peak) * (H - 12) - 6
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
  const area = path ? `${path} L${W - PAD},${H} L${PAD},${H} Z` : ''

  return (
    <div className="modal__scrim" onClick={onClose} role="presentation">
      <div
        className="chartmodal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={metric.label}
      >
        <div className="chartmodal__head">
          <div>
            <div className="modal__title">{metric.label}</div>
            <div className="modal__subtitle">
              {rangeLabel} · {UNIT_NOTE[unit]}
            </div>
          </div>
          <div className="chartmodal__total">
            <div className="chartmodal__totalvalue" style={{ color: metric.color }}>
              {periodTotal === null && points === null ? '—' : nf(total)}
            </div>
            <div className="chartmodal__totallabel">in period</div>
          </div>
          <button className="drawer__closebtn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {error && <div className="modal__error">{error}</div>}

        {points === null && !error && <div className="empty-note chartmodal__empty">Loading…</div>}

        {points !== null && charted === 0 && (
          <div className="empty-note chartmodal__empty">
            Nothing in this period.
            {metric.eventsOnly && ' This one only counts events since the webhook went live.'}
          </div>
        )}

        {points !== null && charted > 0 && (
          <>
            <div className="chartmodal__plot">
              <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`${metric.label} over ${rangeLabel}`}>
                <path d={area} fill={metric.color} opacity="0.12" />
                <path
                  d={path}
                  fill="none"
                  stroke={metric.color}
                  strokeWidth="2.5"
                  vectorEffect="non-scaling-stroke"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="chartmodal__peak">peak {nf(peak)}</div>
            </div>

            <div className="chartmodal__axis">
              {points.map((p, i) => (
                <div
                  className="chartmodal__tick"
                  key={`${p.label}-${i}`}
                  title={`${p.label}: ${nf(p.value)}`}
                >
                  {/* Only every other label on a crowded axis, so they stay readable. */}
                  {points.length <= 12 || i % 2 === 0 ? p.label : ''}
                </div>
              ))}
            </div>
          </>
        )}

        {undated > 0 && (
          <div className="micro chartmodal__undated">
            {nf(undated)} of these carry no date and so cannot be plotted — the chart shows the{' '}
            {nf(charted)} that can.
          </div>
        )}

        {/*
          Who, not how many — the list the chart cannot give.

          The boxes answer "how many" and the chart answers "when". A count of
          six cancelling trials only becomes something you can act on once you
          know whose they are, so the records sit under the chart rather than on
          a screen of their own.

          Newest first, on the date this metric is actually about — the column
          header names it, because "newest" means a different field for a new
          account than for an expired subscription. 25 a page.
        */}
        <div className="mrows">
          <div className="mrows__head">
            <div className="card-label">Records</div>
            <div className="micro">
              {rowsLoading
                ? 'Loading…'
                : page && page.rows.length > 0
                  ? `${nf(pageIndex * METRIC_PAGE + 1)}–${nf(pageIndex * METRIC_PAGE + page.rows.length)}${page.hasMore ? '' : ` of ${nf(pageIndex * METRIC_PAGE + page.rows.length)}`} · newest first`
                  : ''}
            </div>
          </div>

          {rowsError && <div className="modal__error">{rowsError}</div>}

          {page?.identityNote && <p className="micro mrows__note">{page.identityNote}</p>}

          {!rowsError && !rowsLoading && page && page.rows.length === 0 && (
            <p className="empty-note">
              No records behind this figure for {rangeLabel.toLowerCase()}. The chart above is
              plotted from the same query, so an empty list here means an empty period rather than
              a failed read.
            </p>
          )}

          {page && page.rows.length > 0 && (
            <div className="mrows__table">
              <div className="mrows__row mrows__row--head">
                <div>Who</div>
                <div title="Where RevenueCat last saw the account">Country</div>
                <div title="The language Skida writes in for that country">Language</div>
                <div>{metricDateLabel(metric.key)}</div>
                <div>Detail</div>
              </div>
              {page.rows.map((r) => (
                <div className="mrows__row" key={r.key}>
                  <div className="mrows__who">
                    {/* A name when the product project is connected, an email
                        when there is no name, and the account id when there is
                        neither — never a blank cell, because a blank looks like
                        a missing person rather than a missing name. */}
                    <span className="mrows__name">{r.name || r.email || r.uid || '—'}</span>
                    {r.name && r.email && <span className="mrows__sub">{r.email}</span>}
                  </div>
                  <div>
                    <Flag code={r.uid ? places.get(r.uid)?.country : null} />
                  </div>
                  <div>
                    <LanguageFlag language={r.uid ? places.get(r.uid)?.language : null} />
                  </div>
                  <div className="mrows__date">{r.at ? formatDate(r.at.slice(0, 10)) : '—'}</div>
                  <div className="mrows__detail">{r.detail || '—'}</div>
                </div>
              ))}
            </div>
          )}

          {page && (page.hasMore || pageIndex > 0) && (
            <div className="mrows__pager">
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => setPageIndex((n) => Math.max(0, n - 1))}
                disabled={pageIndex === 0 || rowsLoading}
              >
                ← Previous
              </button>
              <span className="micro">Page {pageIndex + 1}</span>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => setPageIndex((n) => n + 1)}
                disabled={!page.hasMore || rowsLoading}
              >
                Next →
              </button>
            </div>
          )}
        </div>

        <div className="micro chartmodal__basis">{metric.basis}</div>
      </div>
    </div>
  )
}
